import type { ReactNode } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { TextButton } from "@/components/ui/pressable";
import { TableCell, TableHead, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { SortableTableHead } from "@/modules/shared/chrome";
import type { GridColumn } from "@/modules/shared/dataGrid/gridTypes";
import {
  ClientGridHeaderCell,
  ServerGridHeaderCell,
  type GridHeaderState,
} from "@/modules/shared/dataGrid/GridHeaderCell";

/** From useDataGrid: header filters + the columns icon. */
export type GridHeaderBinding = {
  header: GridHeaderState;
  columnsIcon: ReactNode;
};

export type GridSort = { field: string; order: "ASC" | "DESC" };

const sortFieldOf = <Row,>(column: GridColumn<Row>) =>
  column.sortField ?? column.field ?? column.id;

/** Header for tables whose rows are already loaded (no ra-core list). */
const ClientSortHead = <Row,>({
  column,
  sort,
  onSort,
}: {
  column: GridColumn<Row>;
  sort: GridSort | null;
  onSort: (sort: GridSort) => void;
}) => {
  const field = sortFieldOf(column);
  const active = sort?.field === field;
  const order = active ? sort.order : "ASC";
  return (
    <TableHead
      className={cn(
        "text-muted-foreground",
        column.align === "right" && "text-right",
        column.headClassName,
      )}
      aria-sort={
        active ? (order === "ASC" ? "ascending" : "descending") : undefined
      }
    >
      <TextButton
        size="xs"
        className={cn(
          "h-8 uppercase tracking-wide",
          column.align === "right" && "ml-auto flex",
          active && "text-foreground",
        )}
        onClick={() =>
          onSort({
            field,
            order: active ? (order === "ASC" ? "DESC" : "ASC") : "ASC",
          })
        }
      >
        {column.label}
        {active ? (
          order === "ASC" ? (
            <ArrowUp className="opacity-70" />
          ) : (
            <ArrowDown className="opacity-70" />
          )
        ) : null}
      </TextButton>
    </TableHead>
  );
};

/**
 * Header row for the visible columns. Sortable columns use the ra-core
 * list sort, or `sort`/`onSort` for tables with rows already loaded.
 */
export const GridHeaderRow = <Row,>({
  columns,
  leading,
  trailing,
  sort,
  onSort,
  grid,
}: {
  columns: GridColumn<Row>[];
  /** Extra cells before the columns (e.g. a select-all checkbox). */
  leading?: ReactNode;
  /** Extra cells after the columns (e.g. an actions column). */
  trailing?: ReactNode;
  sort?: GridSort | null;
  onSort?: (sort: GridSort) => void;
  /** Pass the grid to sort/filter from the header and show the columns icon. */
  grid?: GridHeaderBinding;
}) => (
  <TableRow className="hover:bg-transparent">
    {leading}
    {columns.map((column) =>
      grid && (column.sortable || column.filterable) ? (
        onSort ? (
          <ClientGridHeaderCell
            key={column.id}
            column={column}
            header={grid.header}
            sort={sort ?? null}
            onSort={onSort}
          />
        ) : (
          <ServerGridHeaderCell
            key={column.id}
            column={column}
            header={grid.header}
          />
        )
      ) : column.sortable && onSort ? (
        <ClientSortHead
          key={column.id}
          column={column}
          sort={sort ?? null}
          onSort={onSort}
        />
      ) : column.sortable ? (
        <SortableTableHead
          key={column.id}
          field={sortFieldOf(column)}
          label={column.label}
          align={column.align}
          className={column.headClassName}
        />
      ) : (
        <TableHead
          key={column.id}
          className={cn(
            "text-muted-foreground",
            column.align === "right" && "text-right",
            column.headClassName,
          )}
        >
          {column.label}
        </TableHead>
      ),
    )}
    {trailing}
    {grid ? (
      <TableHead className="w-10 px-1 text-right">{grid.columnsIcon}</TableHead>
    ) : null}
  </TableRow>
);

/** Shared list row height — Accounts, Projects, Tickets, and other data grids. */
export const GRID_LIST_ROW_CLASS =
  "h-11 [&>td]:h-11 [&>td]:max-h-11 [&>td]:py-0 [&>td]:align-middle";

export const GRID_LIST_CELL_CLASS =
  "h-11 max-h-11 py-0 align-middle overflow-hidden whitespace-nowrap";

/** Empty cell under the header's columns icon (keeps rows aligned). */
export const GridRowEnd = () => (
  <TableCell className={cn(GRID_LIST_CELL_CLASS, "w-10 px-0")} />
);

/**
 * Cells of one row, in the visible columns' order. Every list row is the
 * same height (h-11); cell content stays on one line.
 */
export const GridCells = <Row,>({
  columns,
  row,
}: {
  columns: GridColumn<Row>[];
  row: Row;
}) => (
  <>
    {columns.map((column) => (
      <TableCell
        key={column.id}
        className={cn(
          GRID_LIST_CELL_CLASS,
          column.align === "right" && "text-right",
          column.cellClassName,
        )}
      >
        {column.cell(row)}
      </TableCell>
    ))}
  </>
);
